import { useDispensary } from 'src/hooks/use-dispensary';
import { useStores } from 'src/hooks/use-stores';
import { useEnterpriseMobileAppSettingsQuery } from 'types/graphql';

const useHasMobileApp = (): boolean => {
  const { apolloClient } = useStores();
  const dispensary = useDispensary();
  const enterpriseId = dispensary.retailer?.enterpriseId;

  const { data } = useEnterpriseMobileAppSettingsQuery({
    client: apolloClient,
    variables: { enterpriseId: enterpriseId ?? '' },
    skip: !enterpriseId,
  });

  const hasMobileApp =
    data?.enterpriseMobileAppSettings?.iosBundleIdentifier ??
    data?.enterpriseMobileAppSettings?.androidPackageName ??
    '';
  return !!hasMobileApp.length;
};

/**
 * Same as useHasMobileApp, but also exposes the Apollo query's loading state.
 *
 * Needed when a caller must not make a *persisting* decision based on hasMobileApp before the
 * underlying query has resolved -- useHasMobileApp returns `false` while the query is still
 * in flight, so "no mobile app" and "still loading" are indistinguishable to consumers. The
 * Channels wizard step is one such caller: without `isLoading`, an admin who clicks Continue
 * in the sub-second window before the query resolves would commit the loading-state default
 * and silently drop a previously-persisted `mobile` channel.
 *
 * All other existing callers render-only and don't need this -- they should keep using the
 * plain boolean hook above.
 */
export const useHasMobileAppWithLoading = (): { hasMobileApp: boolean; isLoading: boolean } => {
  const { apolloClient } = useStores();
  const dispensary = useDispensary();
  const enterpriseId = dispensary.retailer?.enterpriseId;

  const { data, loading } = useEnterpriseMobileAppSettingsQuery({
    client: apolloClient,
    variables: { enterpriseId: enterpriseId ?? '' },
    skip: !enterpriseId,
  });

  const hasMobileApp =
    data?.enterpriseMobileAppSettings?.iosBundleIdentifier ??
    data?.enterpriseMobileAppSettings?.androidPackageName ??
    '';
  return { hasMobileApp: !!hasMobileApp.length, isLoading: loading };
};

export default useHasMobileApp;
